// absolute imports
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  split,
} from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import * as Sentry from '@sentry/react';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef } from 'react';

// relative imports
import { cache, persistor } from '../../data/apollo/cache/cache';
import authLink from '../../data/apollo/client/authLink';
import carfTransmissionMockLink from '../../data/apollo/client/carfTransmissionMockLink';
import clientTypeDefs from '../../data/apollo/client/clientTypeDefs';
import errorLink from '../../data/apollo/client/errorLink';
import {
  ledgibleAPILink,
  ledgibleCMSLink,
} from '../../data/apollo/client/httpLink';
import restLink from '../../data/apollo/client/restLink';
import retryLink from '../../data/apollo/client/retryLink';

const AuthorizedApolloProvider = ({ children }) => {
  const auth0 = useAuth0();
  const { getAccessTokenSilently, loginWithRedirect } = auth0;
  const clientRef = useRef(null);

  // Memoize link split to prevent recreation
  const httpLinkSplit = useMemo(
    () =>
      split(
        (operation) => operation.getContext().client === 'ledgible-cms',
        ledgibleCMSLink,
        ledgibleAPILink,
      ),
    [],
  );

  /* Create Apollo Link array - memoized to prevent recreation */
  const link = useMemo(
    () =>
      ApolloLink.from([
        errorLink(getAccessTokenSilently),
        authLink(getAccessTokenSilently, loginWithRedirect),
        restLink,
        retryLink,
        carfTransmissionMockLink,
        httpLinkSplit,
      ]),
    [getAccessTokenSilently, loginWithRedirect, httpLinkSplit],
  );

  const client = useMemo(() => {
    // Stop and clean up previous client if it exists
    if (clientRef.current) {
      try {
        clientRef.current.stop();
      } catch {
        // Ignore errors
      }
    }

    const newClient = new ApolloClient({
      link,
      cache,
      typeDefs: clientTypeDefs,
      defaultOptions: {
        watchQuery: { errorPolicy: 'none', returnPartialData: false },
        query: { errorPolicy: 'none' },
        mutate: { errorPolicy: 'none' },
      },
      connectToDevTools: import.meta.env.DEV,
    });

    // callback for when apollo store is cleared (eg for logout);
    newClient.onClearStore(async () => {
      try {
        await persistor.purge();
      } catch (persistorPurgeError) {
        // Capture as warning
        Sentry.captureException(persistorPurgeError, {
          level: 'warning',
          tags: {
            component: 'AuthorizedApolloProvider',
            kind: 'indexeddb_purge',
          },
        });
      }
    });

    clientRef.current = newClient;
    return newClient;
  }, [link]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (clientRef.current) {
        try {
          clientRef.current.stop();
        } catch {
          // Ignore errors
        }
        clientRef.current = null;
      }
    };
  }, []);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default AuthorizedApolloProvider;

AuthorizedApolloProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
